import { useTranslation } from "react-i18next";

import { disableSentry, initSentry } from "../../lib/sentry";
import { optInAnalytics, optOutAnalytics } from "../../lib/posthog";

import { setConsent, useConsent, type ConsentCategory } from "./store";

export function ConsentToggles() {
  const { t } = useTranslation();
  const consent = useConsent();

  function toggle(category: ConsentCategory, next: boolean) {
    setConsent(category, next);
    if (category === "analytics") {
      if (next) optInAnalytics().catch((e) => console.error("[consent] optInAnalytics failed", e));
      else optOutAnalytics();
    } else if (category === "crashReports") {
      if (next) initSentry().catch((e) => console.error("[consent] initSentry failed", e));
      else disableSentry();
    }
  }

  return (
    <section className="mt-8 space-y-4">
      <h2 className="text-sm font-semibold text-fg-mute">{t("consent.heading")}</h2>

      <Row label={t("consent.location_label")} body={t("consent.location_body")} required />

      <Row
        label={t("consent.analytics_label")}
        body={t("consent.analytics_body")}
        checked={consent.analytics}
        onChange={(v) => toggle("analytics", v)}
      />

      <Row
        label={t("consent.crash_label")}
        body={t("consent.crash_body")}
        checked={consent.crashReports}
        onChange={(v) => toggle("crashReports", v)}
      />
    </section>
  );
}

type RowProps = {
  label: string;
  body: string;
  checked?: boolean;
  required?: boolean;
  onChange?: (next: boolean) => void;
};

function Row({ label, body, checked, required, onChange }: RowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-line bg-surface p-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{label}</p>
        <p className="mt-1 text-xs text-fg-mute">{body}</p>
      </div>
      <label className="inline-flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          aria-label={label}
          checked={required ? true : !!checked}
          disabled={required}
          onChange={(e) => onChange?.(e.target.checked)}
          className="h-4 w-4 rounded border-line"
        />
      </label>
    </div>
  );
}
